import React, { useState, useMemo } from 'react';
import useDescriptionGenerator from '../hooks/useDescriptionGenerator';

/**
 * AI Description Generator Component
 * Provides UI for generating product descriptions using AI
 * 
 * @param {Object} props
 * @param {string} props.productName - Product name
 * @param {string[]} props.categories - Selected categories
 * @param {Array} props.subcategories - Selected subcategories with subcategory property
 * @param {Function} props.onDescriptionGenerated - Callback when description is generated
 */
const AIDescriptionGenerator = ({
  productName = '',
  categories = [],
  subcategories = [],
  onDescriptionGenerated
}) => {
  const [tone, setTone] = useState('professional');
  const [lastGenerated, setLastGenerated] = useState('');
  const { generateDescription, isGenerating, error, setError } = useDescriptionGenerator();

  // Check if ready to generate
  const isReady = useMemo(() => {
    return productName.trim().length > 0 && 
           (categories.length > 0 || subcategories.length > 0);
  }, [productName, categories, subcategories]);

  // Handle generate button click
  const handleGenerate = async () => {
    if (!isReady || isGenerating) return;

    setError(''); // Clear previous errors
    
    const result = await generateDescription({
      productName,
      categories,
      subcategories,
      tone
    });

    if (result) {
      setLastGenerated(result);
      if (onDescriptionGenerated) {
        onDescriptionGenerated(result);
      }
    }
  };

  // Tone button styles
  const getToneButtonStyle = (toneName) => {
    const isActive = tone === toneName;
    return {
      padding: '8px 16px',
      border: isActive ? '2px solid #1E5B3A' : '1px solid #ced4da',
      borderRadius: '20px',
      backgroundColor: isActive ? '#1E5B3A' : '#ffffff',
      color: isActive ? '#ffffff' : '#495057',
      fontSize: '14px',
      fontWeight: isActive ? '600' : '400',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginRight: '8px',
      marginBottom: '8px'
    };
  };

  return (
    <div style={{
      border: '1.5px solid #1E5B3A',
      borderRadius: '10px',
      padding: '16px 20px',
      backgroundColor: '#f0faf4',
      marginTop: '6px'
    }}>
      {/* Header with AI badge */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{
          backgroundColor: '#1E5B3A',
          color: '#ffffff',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          marginRight: '10px'
        }}>
          AI
        </span>
        <span style={{ fontSize: '15px', fontWeight: '600', color: '#1E5B3A' }}>
          Auto-generate Description
        </span>
      </div>

      {/* Warning if name or category missing */}
      {!isReady && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          color: '#856404',
          padding: '10px 14px',
          borderRadius: '6px',
          fontSize: '13px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
          <span style={{ marginRight: '8px', fontSize: '16px' }}>⚠</span>
          <div>
            <strong>Required:</strong> Please enter product name and select at least one category or subcategory to generate description.
          </div>
        </div>
      )}

      {/* Tone selector */}
      <div style={{ marginBottom: '14px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#333',
          marginBottom: '8px'
        }}>
          Tone:
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <button
            type="button"
            style={getToneButtonStyle('professional')}
            onClick={() => setTone('professional')}
            disabled={isGenerating}
          >
            Professional
          </button>
          <button
            type="button"
            style={getToneButtonStyle('enthusiastic')}
            onClick={() => setTone('enthusiastic')}
            disabled={isGenerating}
          >
            Enthusiastic
          </button>
          <button
            type="button"
            style={getToneButtonStyle('minimal')}
            onClick={() => setTone('minimal')}
            disabled={isGenerating}
          >
            Minimal
          </button>
          <button
            type="button"
            style={getToneButtonStyle('luxury')}
            onClick={() => setTone('luxury')}
            disabled={isGenerating}
          >
            Luxury
          </button>
        </div>
      </div>

      {/* Generate button */}
      <button
        type="button"
        onClick={handleGenerate}
        disabled={!isReady || isGenerating}
        style={{
          backgroundColor: (!isReady || isGenerating) ? '#5B5040' : '#1E5B3A',
          color: '#ffffff',
          border: 'none',
          borderRadius: '6px',
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: (!isReady || isGenerating) ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          transition: 'background-color 0.2s ease',
          opacity: (!isReady || isGenerating) ? 0.6 : 1
        }}
      >
        {isGenerating ? (
          <>
            <div style={{
              width: '14px',
              height: '14px',
              border: '2px solid #ffffff',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'ai-spin 0.6s linear infinite',
              marginRight: '8px'
            }} />
            Generating...
          </>
        ) : (
          <>
            <span style={{ marginRight: '6px' }}>
              {lastGenerated ? '↺' : '✦'}
            </span>
            {lastGenerated ? 'Regenerate Description' : 'Generate Description'}
          </>
        )}
      </button>

      {/* Success message */}
      {lastGenerated && !isGenerating && !error && (
        <div style={{
          marginTop: '12px',
          color: '#2E7D4F',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ marginRight: '6px' }}>✓</span>
          Description applied successfully
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{
          marginTop: '12px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          padding: '10px 14px',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* CSS animation for spinner */}
      <style>{`
        @keyframes ai-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AIDescriptionGenerator;
